import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const BrandPitchesContext = createContext(null);

export const BrandPitchesProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/brand-pitches');
      setItems(data || []);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Manager-only mutations
  const ensurePitch = async (brand_id, talent_id) => {
    const { data } = await api.post('/brand-pitches', { brand_id, talent_id });
    setItems((prev) => (prev.find((p) => p.id === data.id) ? prev.map((p) => p.id === data.id ? data : p) : [...prev, data]));
    return data;
  };

  const updatePitch = async (id, patch) => {
    const { data } = await api.patch(`/brand-pitches/${id}`, patch);
    setItems((prev) => prev.map((p) => p.id === id ? data : p));
    return data;
  };

  const removePitch = async (id) => {
    await api.delete(`/brand-pitches/${id}`);
    setItems((prev) => prev.filter((p) => p.id !== id));
  };

  const addComms = async (id, text) => {
    const { data } = await api.post(`/brand-pitches/${id}/comms`, { text });
    setItems((prev) => prev.map((p) => p.id === id ? data : p));
    return data;
  };

  const removeComms = async (id, comms_id) => {
    const { data } = await api.delete(`/brand-pitches/${id}/comms/${comms_id}`);
    setItems((prev) => prev.map((p) => p.id === id ? data : p));
  };

  const reorderForTalent = async (talent_id, order) => {
    const { data } = await api.post('/brand-pitches/reorder', { talent_id, order });
    // replace all pitches for this talent with the returned set
    setItems((prev) => {
      const others = prev.filter((p) => p.talent_id !== talent_id);
      return [...others, ...(data || [])];
    });
  };

  // Sort helper: highest stars first; within tied stars, lowest position (manual order)
  const forTalent = useMemo(() => (talent_id) => {
    const rows = items.filter((p) => p.talent_id === talent_id);
    return [...rows].sort((a, b) => {
      if ((b.stars || 0) !== (a.stars || 0)) return (b.stars || 0) - (a.stars || 0);
      return (a.position || 0) - (b.position || 0);
    });
  }, [items]);

  const forBrand = useMemo(() => (brand_id) => items.filter((p) => p.brand_id === brand_id), [items]);

  return (
    <BrandPitchesContext.Provider value={{
      loading, items, refresh,
      ensurePitch, updatePitch, removePitch, addComms, removeComms, reorderForTalent,
      forTalent, forBrand,
    }}>
      {children}
    </BrandPitchesContext.Provider>
  );
};

export const useBrandPitches = () => {
  const c = useContext(BrandPitchesContext);
  if (!c) throw new Error('useBrandPitches must be used within BrandPitchesProvider');
  return c;
};

export const BRAND_PITCH_STATUSES = [
  { id: 'pitched', label: 'pitched', color: '#38bdf8' },
  { id: 'follow_up', label: 'follow-up', color: '#f59e0b' },
  { id: 'interested', label: 'interested', color: '#d394ab' },
  { id: 'declined', label: 'declined', color: '#94a3b8' },
  { id: 'converted', label: 'converted', color: '#059669' },
];
